/**
 * Shared section + freshness atoms for SEO pages.
 *
 *  - SeoSection: the standard section wrapper (eyebrow + heading + optional
 *    intro + children) in the marketing vocabulary, so pages stay consistent
 *    and DRY.
 *  - LastReviewedDate: a small "Last reviewed <date>" line.
 *  - ArticleMetadata: an honest byline strip ("Reviewed by the PCNshark team"
 *    + last-reviewed date) — no fabricated authors.
 */
import type { ReactNode } from "react";

type Tone = "paper" | "paper-2" | "white";

const TONE_CLASS: Record<Tone, string> = {
  paper: "bg-paper",
  "paper-2": "bg-paper-2",
  white: "bg-white",
};

export function SeoSection({
  id,
  eyebrow,
  heading,
  accent,
  intro,
  tone = "paper",
  children,
}: {
  id?: string;
  eyebrow?: string;
  heading?: string;
  accent?: string;
  intro?: string[];
  tone?: Tone;
  children?: ReactNode;
}) {
  return (
    <section id={id} className={`border-b rule ${TONE_CLASS[tone]}`}>
      <div className="max-w-[1240px] mx-auto px-6 lg:px-8 py-16">
        {eyebrow && (
          <div className="font-mono text-[11px] uppercase tracking-wider ink-3 mb-3">
            {eyebrow}
          </div>
        )}
        {heading && (
          <h2 className="font-display font-semibold text-[28px] lg:text-[34px] tracking-[-0.025em] leading-[1.06] max-w-[26ch]">
            {heading}
            {accent && (
              <>
                {" "}
                <span
                  className="font-serif-display italic font-normal"
                  style={{ color: "var(--shark-accent-ink)" }}
                >
                  {accent}
                </span>
              </>
            )}
          </h2>
        )}
        {intro?.map((p, i) => (
          <p
            key={i}
            className="mt-4 text-[15px] ink-2 leading-relaxed max-w-[68ch]"
          >
            {p}
          </p>
        ))}
        {children}
      </div>
    </section>
  );
}

/** Format an ISO date (YYYY-MM-DD) as e.g. "June 25, 2026" in UTC. */
export function formatReviewDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
}

export function LastReviewedDate({ date }: { date: string }) {
  return (
    <span className="font-mono text-[11px] uppercase tracking-wider ink-3">
      Last reviewed{" "}
      <time dateTime={date} className="ink-2">
        {formatReviewDate(date)}
      </time>
    </span>
  );
}

/**
 * Site-wide reviewer credit for the SEO pages. Change it here to update the
 * byline everywhere. Only state a role/profile that is actually accurate — no
 * fabricated credentials. Add `profileUrl` here if/when a public profile exists.
 */
export const SITE_REVIEWER = { name: "Mason", role: "Founder" } as const;

/**
 * Byline + freshness strip. Defaults to the honest organization-level reviewer
 * ("the PCNshark product team"). A named expert can be added later by passing
 * `reviewer` — no per-page layout change needed. Do not fabricate credentials:
 * only pass a `role`/`profileUrl` that is actually approved.
 */
export function ArticleMetadata({
  lastReviewed,
  reviewer,
}: {
  lastReviewed: string;
  reviewer?: { name: string; role?: string; profileUrl?: string };
}) {
  const byline = reviewer ? (
    <span>
      Reviewed by{" "}
      {reviewer.profileUrl ? (
        <a
          href={reviewer.profileUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="ink-2 ulink hover:text-[color:var(--ink)]"
        >
          {reviewer.name}
        </a>
      ) : (
        <span className="ink-2">{reviewer.name}</span>
      )}
      {reviewer.role ? `, ${reviewer.role}` : ""}
    </span>
  ) : (
    <span>Reviewed by the PCNshark product team</span>
  );
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 font-mono text-[11px] uppercase tracking-wider ink-3">
      {byline}
      <span aria-hidden>·</span>
      <LastReviewedDate date={lastReviewed} />
    </div>
  );
}
